import { FC, useState } from 'react'
import { Col } from 'antd'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'

import PerText from '../Text'
import PerIcon from '../Icon'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import { useDropzone } from 'react-dropzone'
import { TargetModalType } from './types'
import { useTargetsModalStore } from './TargetsModalStore'
import { UPLOAD_FILE_MUTATION } from '../../../graphql/objectives/uploadObjectiveAttachment'
import { getObjectiveAttachmentHref } from '../../../modules/meeting/util/hrefUtils'
import { useObjectiveAttachmentHelper, atobUtf8 } from '../../../shared/useObjectiveAttachmentHelper'
import { hasValidationErrors, getValidationErrors } from '../../../shared/graphqlErrorHelper'
import './index.less'

const MAX_SIZE = 10485760 /* 10MB */

interface TargetsModalAttachmentProps {
  showDropZone?: boolean
}

export const TargetsModalAttachment: FC<TargetsModalAttachmentProps> = ({ showDropZone = false }) => {
  const { t } = useTranslation()
  const [uploadFile] = useMutation(UPLOAD_FILE_MUTATION)
  const { extractHumanReadableFileName } = useObjectiveAttachmentHelper(atobUtf8)

  const [error, setError] = useState<string>('')

  const [currentObjective, currentAttachment, setAttachment, modalProps, targetModalType] = useTargetsModalStore(
    (state) => [state.currentObjective, state.currentAttachment, state.setAttachment, state.modalProps, state.targetModalType],
    shallow,
  )

  const handleUpload = async ([file], inputRef: React.RefObject<HTMLElement>) => {
    if (!modalProps.employeeId) {
      setError(t('targetModal.userNotFound'))
    } else {
      if (!!file) {
        setError('')
        const { data, errors } = await uploadFile({
          variables: { file, employeeIdOfMeetingNotLoggedInEmployeeId: modalProps.employeeId },
          context: {
            headers: {
              'Apollo-Require-Preflight': 'true',
            },
          },
        })

        if (errors) {
          if (hasValidationErrors(errors)) {
            const valErrors = getValidationErrors(errors)
            if (valErrors && valErrors[0] && valErrors[0].message) {
              setError(valErrors[0].message)
            } else {
              setError(errors[0].message)
            }
          }
        } else {
          !!inputRef.current && inputRef.current.blur()
          setAttachment(data.uploadObjectiveAttachment)
        }
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive, fileRejections, rootRef } = useDropzone({
    onDrop: ([file]) => handleUpload([file], rootRef),
    multiple: false,
    maxSize: MAX_SIZE,
  })

  const isFileTooLarge = fileRejections.length > 0 && fileRejections[0].file.size > MAX_SIZE

  const canRemoveAttachment = targetModalType !== TargetModalType.viewTarget

  return (
    <PerFlexRow align="top">
      {!!showDropZone && (
        <>
          <Col span={8}>
            <PerText appearance="label-small" mb="xxs">
              {t('targetModal.attachment')}
            </PerText>
            <PerFlexRow>
              <div {...getRootProps()} className="dropzone" data-e2e-upload-attachment>
                <input {...getInputProps()} />
                {isDragActive ? <p>{t('okr.objective.modal.dropzoneHovered')}</p> : <p>{t('okr.objective.modal.dropzone')}</p>}
                {isFileTooLarge && <div className="danger">{t('okr.objective.modal.tooLarge')}</div>}
                {error && <div className="danger">{error}</div>}
              </div>
            </PerFlexRow>
          </Col>
          <Col span={1} />
        </>
      )}
      <Col span={!!showDropZone ? 15 : 24}>
        {!!currentAttachment && (
          <>
            {!showDropZone && (
              <PerText appearance="label-small" mt="m">
                {t('targetModal.attachment')}
              </PerText>
            )}
            <PerFlexRow align="middle" justify="start" mt={!!showDropZone ? 'm' : 'none'}>
              <PerIcon name="attachment_fill" size={16} />
              <div
                className={classNames({
                  'per-attachment--filename-wrapper': !!showDropZone,
                  'per-attachment--filename-wrapper no-dropzone': !showDropZone,
                })}
              >
                <PerText fontWeight="bold" className="per-attachment--filename" ml="xxxs">
                  {extractHumanReadableFileName(currentAttachment)}
                </PerText>
              </div>
              {!!modalProps.employeeId && !!currentObjective && !!currentAttachment && !!currentObjective.attachmentDownloadToken && (
                <PerButtonCircle
                  color="primary"
                  variant={'pastel'}
                  icon="view_fill"
                  ml="xs"
                  asLink={getObjectiveAttachmentHref(modalProps.employeeId, currentAttachment, currentObjective.attachmentDownloadToken)}
                />
              )}
              {canRemoveAttachment && (
                <PerButtonCircle
                  icon="remove_stroke"
                  size="small"
                  variant="pastel"
                  color="secondary"
                  ml="xs"
                  onClick={() => setAttachment(null)}
                />
              )}
            </PerFlexRow>
          </>
        )}
      </Col>
    </PerFlexRow>
  )
}
